import { isDelawareRegisteredAgentAddress } from "components/fileTax/DelawareRegisteredAgentAddresses";
import { emailValidationRegex } from "constants/emailValidationRegex";
import {
  ADDRESS_DETAIL_KEY,
  AUTOFILL_KEY_TO_DETAIL_KEY,
  AUTOFILL_PICKER_FIELD_TYPES,
  DELAWARE_AGENT_ADDRESS_ERROR,
  DIRECTORS_PICKER_FIELD_TYPE,
  EMAIL_FIELD_TYPE,
  INVALID_EMAIL_ERROR,
  PRINCIPAL_PLACE_OF_BUSINESS_FIELD_KEY,
  REQUIRED_FIELD_ERROR,
  UPLOAD_OR_LINK_ERROR,
  VALUE_FIELD_TYPES,
} from "constants/filingFormFields";
import {
  FIELD_TYPE,
  RepeatableField,
  Section,
  SectionField,
} from "types/Models/Filing";
import { hasUploadOrLinkValue } from "util/booksHandoff/hasUploadOrLinkValue";
import { hasSelectedDirector } from "./hasSelectedDirector";
import { withRepeatId } from "./withRepeatId";

type Values = Record<string, unknown>;

/** A field that blocks submit while `isFilled` is false */
export type FieldCheck = {
  fieldKey: string;
  sectionKey: string;
  isFilled: boolean;
  error: string;
};

type FieldCheckOptions = {
  sections: Section[];
  values: Values;
  /** From `getFieldKeysOnPage`: only fields the viewer can see are checked */
  fieldKeysOnPage: Set<string>;
  /** Also check `required_on_completion` sections */
  isCompletingTask: boolean;
  /** Highest row id per section, including rows added on screen but not saved */
  addedRepeatableIds?: Record<string, number>;
};

type FieldEntry = {
  field: SectionField;
  row: RepeatableField | null;
  sectionKey: string;
};

type Answer = { isFilled: boolean; error: string } | null;

const NO_ADDED_IDS: Record<string, number> = {};

const hasValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (typeof value === "number") {
    return true;
  }

  return Boolean(value);
};

const toAnswer = (isFilled: boolean, error: string): Answer => ({
  isFilled,
  error,
});

// Saved rows plus rows added on screen (ids above the saved max); removed rows are not on the page
const getRows = (section: Section, addedMaxId: number): RepeatableField[] => {
  const savedRows = section.repeatable_fields ?? [];
  const [templateRow] = savedRows;

  if (!templateRow) {
    return [];
  }

  const savedMaxId = Math.max(...savedRows.map(({ id }) => id));
  const addedRows = Array.from(
    { length: Math.max(addedMaxId - savedMaxId, 0) },
    (_, index) => savedMaxId + index + 1
  ).map((id) => ({
    ...templateRow,
    id,
    selected_autofill_details: undefined,
    fields: templateRow.fields.map((field) => ({
      ...field,
      field_key: withRepeatId(field.field_key, id),
    })),
  }));

  return [...savedRows, ...addedRows];
};

const getEmailAnswer = (field: SectionField, value: unknown): Answer => {
  const email = typeof value === "string" ? value.trim() : "";

  if (!email) {
    return field.required ? toAnswer(false, REQUIRED_FIELD_ERROR) : null;
  }

  return toAnswer(emailValidationRegex.test(email), INVALID_EMAIL_ERROR);
};

const getPickerAnswer = ({ field, row }: FieldEntry, section: Section) => {
  const lookupKey = field.validation_key || field.autofill_key;
  const detailKey = lookupKey ? AUTOFILL_KEY_TO_DETAIL_KEY[lookupKey] : null;

  if (!detailKey) {
    return null;
  }

  const selectedDetails = (row ?? section).selected_autofill_details;
  const requiredError = field.error_message || REQUIRED_FIELD_ERROR;

  if (!selectedDetails?.[detailKey]?.uuid) {
    return toAnswer(false, requiredError);
  }

  const address = selectedDetails.address_details;
  const isDelawareAgentAddress =
    field.field_key === PRINCIPAL_PLACE_OF_BUSINESS_FIELD_KEY &&
    detailKey === ADDRESS_DETAIL_KEY &&
    Boolean(address) &&
    isDelawareRegisteredAgentAddress(address ?? {});

  return toAnswer(!isDelawareAgentAddress, DELAWARE_AGENT_ADDRESS_ERROR);
};

/** null when the field does not block submit at all */
const getAnswer = (
  entry: FieldEntry,
  section: Section,
  values: Values
): Answer => {
  const { field } = entry;
  const value = values[field.field_key];

  if (field.type === EMAIL_FIELD_TYPE) {
    return getEmailAnswer(field, value);
  }

  if (!field.required) {
    return null;
  }

  const requiredError = field.error_message || REQUIRED_FIELD_ERROR;

  if (AUTOFILL_PICKER_FIELD_TYPES.includes(field.type)) {
    return getPickerAnswer(entry, section);
  }

  if (field.type === DIRECTORS_PICKER_FIELD_TYPE) {
    return toAnswer(
      hasSelectedDirector(section, field.validation_key),
      requiredError
    );
  }

  if (field.type === FIELD_TYPE.UPLOAD_OR_LINK) {
    return toAnswer(
      hasUploadOrLinkValue(field.field_key, values),
      UPLOAD_OR_LINK_ERROR
    );
  }

  if (VALUE_FIELD_TYPES.includes(field.type)) {
    return toAnswer(hasValue(value), requiredError);
  }

  return null;
};

/**
 * The one rule for the filing form: every field the viewer can see that
 * must be answered, and whether it is. Submit fails on the unfilled ones and
 * the pending count shows them, so the two always match.
 */
export const getFieldChecks = ({
  sections,
  values,
  fieldKeysOnPage,
  isCompletingTask,
  addedRepeatableIds = NO_ADDED_IDS,
}: FieldCheckOptions): FieldCheck[] =>
  sections
    .filter((section) => isCompletingTask || !section.required_on_completion)
    .flatMap((section) => {
      const entries: FieldEntry[] = [
        ...(section.section_fields ?? []).map((field) => ({
          field,
          row: null,
          sectionKey: section.section_key,
        })),
        ...getRows(
          section,
          addedRepeatableIds[section.section_key] ?? 0
        ).flatMap((row) =>
          (row.fields ?? []).map((field) => ({
            field,
            row,
            sectionKey: section.section_key,
          }))
        ),
      ];

      return entries
        .filter(({ field }) => fieldKeysOnPage.has(field.field_key))
        .flatMap((entry) => {
          const answer = getAnswer(entry, section, values);

          return answer
            ? [
                {
                  fieldKey: entry.field.field_key,
                  sectionKey: entry.sectionKey,
                  ...answer,
                },
              ]
            : [];
        });
    });
